"use client";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBlog } from "@/redux/Slice/blog/BlogSlice";
import FeaturedBlog from "./FeaturedBlog";
import BlogCard from "./BlogCard";
import ErrorMessage from "./ErrorMessage";
import EmptyState from "./EmptyState";
import SkeletonBlogCard from "./SkeletonBlogCard";
import FeaturedBlogSkeleton from "./FeatureBlogSkeleton";
import TrustedCta from "../solution/ad-creation/TrustedCta";

// Main Blog Component
export default function Blog() {
  const { data, pagination, status, loadingMore, error } = useSelector(
    (state) => state.blog,
  );
  const dispatch = useDispatch();

  // Initial fetch — page 1
  useEffect(() => {
    dispatch(getBlog(1));
  }, [dispatch]);

  // Derived from real API pagination
  const currentPage = pagination?.current_page ?? 1;
  const lastPage = pagination?.last_page ?? 1;
  const hasMore = currentPage < lastPage;

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      dispatch(getBlog(currentPage + 1));
    }
  };

  const blogPosts = Array.isArray(data) ? data : [];

  // Pick the most recently created post as the featured post
  const latestBlogForFeature =
    blogPosts.length > 0
      ? blogPosts.reduce((latest, current) =>
          new Date(current.created_at) > new Date(latest.created_at)
            ? current
            : latest,
        )
      : null;

  // All posts except the featured one go into the grid
  const gridPosts = blogPosts.filter(
    (post) => post.id !== latestBlogForFeature?.id,
  );


  // Shared editorial header — reused by the error and main views
  const header = (
    <header className="relative w-full px-6 md:px-16  xl:px-20 pt-16 pb-10 border-b border-gray-200 overflow-hidden">
      {/* Faint grid that fades out downward */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.04) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          WebkitMaskImage: "linear-gradient(to bottom, black, transparent 80%)",
          maskImage: "linear-gradient(to bottom, black, transparent 80%)",
        }}
      />
      <div className="relative flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
        <h1 className="text-3xl font-[600] md:border-none border border-[#0B0B0B] w-fit px-2 py-0.5 md:p-0 rounded-lg tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-[#0B0B0B] to-[#717171] ">
          Blog
        </h1>
        <span className="hidden sm:block self-stretch w-px bg-gray-300" />
        <p className="text-[14px] leading-[1.5] bg-clip-text text-transparent bg-gradient-to-r from-[#000000] to-[#666666]">
          Learn more about Imagive&apos;s ecosystem.
          <br />
          Latest news, insights and tips from the team.
        </p>
      </div>
    </header>
  );

  // Error state
  if (error && status === "failed") {
    return (
      <main className="bg-white w-full min-h-screen">
        {header}
        <div className="w-full px-20 py-20">
          <ErrorMessage error={error} />
        </div>
      </main>
    );
  }

  return (
    <main className="bg-white w-full min-h-screen">
      {header}

      {/* Featured Post — framed row with a vertical label in the gutter */}
      <section className="w-full px-2 md:px-16  xl:px-20 border-b border-[#E2DFD8] ">
        <div className="flex relative lg:border-x border-[#E2DFD8]">
          {/* Vertical "Featured Post" label */}
          <div className="hidden absolute -left-5 top-1/4 -translate-y-1/2 md:flex shrink-0 items-center px-3 bg-white ">
            <span className="[writing-mode:vertical-rl] rotate-180 text-[11px] font-semibold uppercase tracking-[0.2em]  text-[#101010] whitespace-nowrap">
              Featured Post
            </span>
          </div>

          {/* Featured content */}
          <div className="flex-1 min-w-0">
            {status === "loading" ? (
              <FeaturedBlogSkeleton />
            ) : (
              latestBlogForFeature && (
                <FeaturedBlog latestBlogPost={latestBlogForFeature} />
              )
            )}
          </div>
        </div>
      </section>

      {/* Latest Posts */}
      <section className="w-full px-2 md:px-16  xl:px-20 py-16">
        <h2 className="text-[24px] lg:text-[28px] xl:text-[32px] 2xl:text-[36px] 3xl:text-[40px] font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#0B0B0B] to-[#717171] text-center mb-8">
          Latest Posts
        </h2>

        {status === "loading" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 border-l border-t border-gray-200">
            {Array.from({ length: 8 }, (_, i) => (
              <SkeletonBlogCard key={i} />
            ))}
          </div>
        ) : blogPosts.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 border-l border-t border-gray-200">
              {gridPosts.map((post, index) => (
                <BlogCard
                  key={`${post.id ?? post.slug ?? index}-${index}`}
                  post={post}
                />
              ))}
            </div>
          </>
        )}
      </section>
      {/* Load More */}
      <div className="flex justify-center  border-b  pb-14">
        {hasMore ? (
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="flex justify-center  items-center gap-2 rounded-[8px] border border-[#CCC9C2] px-4 py-2 text-xs font-bold text-[#0B0B0B] transition-colors group-hover:border-gray-900 group-hover:bg-gray-900 group-hover:text-white disabled:opacity-60 disabled:hover:bg-transparent disabled:hover:text-gray-900"
          >
            {loadingMore ? (
              <>
                <svg
                  className="animate-spin w-4 h-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
                Loading…
              </>
            ) : (
              <>
                <span className="flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-current" />
                  <span className="w-1 h-1 rounded-full bg-current" />
                </span>
                Load More
              </>
            )}
          </button>
        ) : (
          gridPosts.length > 0 && (
            <p className="text-center text-gray-400 text-sm">
              You&apos;ve seen all Blogs ✓
            </p>
          )
        )}
      </div>

      <div className="-my-10">
        <TrustedCta />
      </div>
    </main>
  );
}
